import { DateTime } from 'luxon';
import { getContentfulField } from 'common/components';

export const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const formatDayName = (dayName) => {
  const fullDayMap = {
    MON: 'Monday',
    TUE: 'Tuesday',
    WED: 'Wednesday',
    THU: 'Thursday',
    FRI: 'Friday',
    SAT: 'Saturday',
    SUN: 'Sunday',
  };

  return fullDayMap[dayName] || dayName;
};

const isPast = (schedule) => {
  const today = DateTime.now().setZone('America/New_York').startOf('day');
  return today > schedule.endDate;
};

const createNYDateTime = (dateStr) =>
  DateTime.fromFormat(dateStr, 'yyyy-MM-dd', {
    zone: 'America/New_York',
  });

const checkDifferentYears = (date1, date2) => {
  if (date1 instanceof DateTime && date2 instanceof DateTime) {
    return date1.year !== date2.year;
  }
  return new Date(date1).getFullYear() !== new Date(date2).getFullYear();
};

const formatDateWithYear = (date, includeYear = false) => {
  return date.toFormat(includeYear ? 'LLL dd, yy' : 'LLL dd');
};

const formatLocaleDateString = (date, includeYear) => {
  /** @type {Intl.DateTimeFormatOptions} */
  const dateOptions = {
    month: 'short',
    day: 'numeric',
    ...(includeYear && { year: '2-digit' }),
  };

  return date.toLocaleDateString('en', dateOptions);
};

const createFormattedTime = ({ hour, minute }) =>
  DateTime.fromObject({ hour, minute }).toFormat('h:mm a');

const getSchedule = (event) => {
  const schedules = event.schedules.map((a) => ({
    ...a,
    startDate: createNYDateTime(a.startDate),
    endDate: createNYDateTime(a.endDate),
  }));

  return (
    schedules.find(
      (schedule, index) => !isPast(schedule) || index === schedule.length - 1
    ) || schedules[schedules.length - 1]
  );
};

const renderDates = (event) => {
  if (event.externalId) {
    const schedule = getSchedule(event);

    const showTitle = getContentfulField(schedule.showTitle);
    const title = getContentfulField(schedule.title);

    if (showTitle && title) {
      return title;
    }

    const includeYear = checkDifferentYears(
      schedule.startDate,
      schedule.endDate
    );

    if (schedule.startDate.equals(schedule.endDate)) {
      return formatDateWithYear(schedule.startDate, includeYear);
    }

    return `${formatDateWithYear(
      schedule.startDate,
      includeYear
    )} - ${formatDateWithYear(schedule.endDate, includeYear)}`;
  }

  const { startDate, endDate } = event;
  const tempStartDate = startDate.split('T')[0];
  const tempEndDate = endDate.split('T')[0];

  const startDateTime = new Date(startDate);
  const endDateTime = new Date(endDate);
  const includeYear = checkDifferentYears(startDate, endDate);

  if (tempStartDate === tempEndDate) {
    return formatLocaleDateString(startDateTime, includeYear);
  }

  return `${formatLocaleDateString(
    startDateTime,
    includeYear
  )} - ${formatLocaleDateString(endDateTime, includeYear)}`;
};

const formatTimeRange = (dayObj) => {
  if (dayObj.isClosed) return '';

  // If the day has sessions, we don't want to show the regular time range
  if (dayObj.sessions && dayObj.hours?.length > 0) {
    return dayObj.hours.map((time) => {
      return DateTime.fromFormat(time, 'HH:mm').toFormat('h:mm a');
    });
  }

  // Regular time range formatting for non-session days
  const openTime = createFormattedTime({
    hour: dayObj.openHour,
    minute: dayObj.openMinute,
  });

  const closeTime = createFormattedTime({
    hour: dayObj.closeHour,
    minute: dayObj.closeMinute,
  });

  return [`${openTime} - ${closeTime}`];
};

const groupDaysByHours = (weekdays) => {
  const hoursToDays = new Map();

  weekdays.forEach((dayObj) => {
    if (dayObj.isClosed) return;

    if (dayObj.sessions && dayObj.hours?.length > 0) {
      // Create a separate entry for each session time
      dayObj.hours.forEach((sessionTime) => {
        const key = JSON.stringify({
          sessionTime,
          isSession: true,
        });

        if (!hoursToDays.has(key)) {
          hoursToDays.set(key, []);
        }
        hoursToDays.get(key).push(formatDayName(dayObj.weekday));
      });
    } else {
      // Handle regular opening hours
      const key = JSON.stringify({
        openHour: dayObj.openHour,
        openMinute: dayObj.openMinute,
        closeHour: dayObj.closeHour,
        closeMinute: dayObj.closeMinute,
        isSession: false,
      });

      if (!hoursToDays.has(key)) {
        hoursToDays.set(key, []);
      }
      hoursToDays.get(key).push(formatDayName(dayObj.weekday));
    }
  });

  return hoursToDays;
};

const getHours = (event) => {
  if (event.externalId) {
    const schedule = getSchedule(event);
    const hoursToDays = groupDaysByHours(schedule.weekdays);

    return Array.from(hoursToDays.entries()).flatMap(([hoursKey, days]) => {
      const hours = JSON.parse(hoursKey);
      const dayGroups = formatDayGroup(days);

      if (hours.isSession) {
        // For session times, create a separate line for each time
        return dayGroups.map(() => {
          if (hours.sessionTime.includes('-')) {
            const [start, end] = hours.sessionTime.split('-');
            const formattedStart = DateTime.fromFormat(start, 'HH:mm').toFormat(
              'h:mm a'
            );
            const formattedEnd = DateTime.fromFormat(end, 'HH:mm').toFormat(
              'h:mm a'
            );
            return `${formattedStart} - ${formattedEnd}`;
          }
          return DateTime.fromFormat(hours.sessionTime, 'HH:mm').toFormat(
            'h:mm a'
          );
        });
      } else {
        // For regular hours, use the existing time range format
        const formattedTime = formatTimeRange({
          openHour: hours.openHour,
          openMinute: hours.openMinute,
          closeHour: hours.closeHour,
          closeMinute: hours.closeMinute,
        });
        return dayGroups.map((dayStr) => `${dayStr}: ${formattedTime}`);
      }
    });
  }

  if (!event.startTime || !event.endTime) return [];
  return [formatTimeRange([event.startTime, event.endTime])];
};

const getHighlightedDates = (event) => {
  if (!event?.schedules) return [];

  // Get all schedules except the primary one
  const additionalSchedules = event.schedules.slice(1);

  return additionalSchedules.map((schedule) => ({
    title: getContentfulField(schedule.title),
    showTitle: getContentfulField(schedule.showTitle),
    date: renderDates({
      startDate: schedule.startDate + 'T00:00:00',
      endDate: schedule.endDate + 'T00:00:00',
    }),
    time: getHours({ ...event, schedules: [schedule] }),
    overline: schedule.titleOverline
      ? getContentfulField(schedule.titleOverline)
      : '',
    hasOverline: !!schedule.titleOverline,
  }));
};

const formatDayGroup = (days) => {
  if (days.length === 0) return [];

  const isConsecutive = (d1, d2) => {
    const i1 = daysOfWeek.indexOf(d1);
    const i2 = daysOfWeek.indexOf(d2);
    return i2 === i1 + 1 || (d1 === 'Sunday' && d2 === 'Monday');
  };

  const runs = days.reduce((acc, day, index) => {
    if (index === 0) {
      return [[day]];
    }

    const lastRun = acc[acc.length - 1];
    const lastDay = lastRun[lastRun.length - 1];

    if (isConsecutive(lastDay, day)) {
      lastRun.push(day);
    } else {
      acc.push([day]);
    }

    return acc;
  }, []);

  if (
    runs.length > 1 &&
    isConsecutive(
      runs[runs.length - 1][runs[runs.length - 1].length - 1],
      runs[0][0]
    )
  ) {
    runs[0] = [...runs[runs.length - 1], ...runs[0]];
    runs.pop();
  }

  return runs.map((run) => {
    if (run.length === 1) return run[0];
    if (run.length === 2) return `${run[0]} & ${run[1]}`;
    return `${run[0]} - ${run[run.length - 1]}`;
  });
};

const formatEventDate = (startDate, endDate) => {
  if (!startDate) return '';

  const now = DateTime.now();
  const currentYear = now.year;

  const formatDate = (date, showYear) => {
    const luxonDate = DateTime.fromJSDate(date);
    return luxonDate.toFormat(showYear ? 'MMM dd, yyyy' : 'MMM dd');
  };

  if (!endDate || startDate.getTime() === endDate.getTime()) {
    const showYear = DateTime.fromJSDate(startDate).year !== currentYear;
    return formatDate(startDate, showYear);
  }

  // Only show years if either date is not in current year OR years don't match
  const startYear = DateTime.fromJSDate(startDate).year;
  const endYear = DateTime.fromJSDate(endDate).year;
  const showYears = startYear !== currentYear || startYear !== endYear;

  return `${formatDate(startDate, showYears)} - ${formatDate(
    endDate,
    showYears
  )}`;
};

const parseEventDate = (dateString) =>
  dateString ? DateTime.fromISO(dateString).toJSDate() : null;

export {
  getSchedule,
  isPast,
  formatEventDate,
  parseEventDate,
  getHours,
  getHighlightedDates,
  renderDates,
  formatDayName,
  formatDayGroup,
  groupDaysByHours,
  formatTimeRange,
};
